/** Renderer de matriz Gaussiana — portado de tool-heatmap/public/app.js */

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function accumulateGaussian(matrix, bins, cx, cy, sigma, weight) {
  const radius = Math.max(1, Math.ceil(sigma * 3));
  const startX = clamp(Math.floor(cx - radius), 0, bins - 1);
  const endX = clamp(Math.ceil(cx + radius), 0, bins - 1);
  const startY = clamp(Math.floor(cy - radius), 0, bins - 1);
  const endY = clamp(Math.ceil(cy + radius), 0, bins - 1);
  const sigma2 = sigma * sigma;

  for (let by = startY; by <= endY; by += 1) {
    for (let bx = startX; bx <= endX; bx += 1) {
      const dx = bx - cx;
      const dy = by - cy;
      const gaussian = Math.exp(-(dx * dx + dy * dy) / (2 * sigma2));
      matrix[by * bins + bx] += weight * gaussian;
    }
  }
}

/**
 * Acumula puntos (x,y) en matriz bins×bins.
 * points: { x, y, weight } en coords de imagen natural.
 */
export function buildPointsHeatmapMatrix(points, imgWidth, imgHeight, settings, bins = 96) {
  const matrix = new Array(bins * bins).fill(0);
  const separationFactor = 1.35 - (settings.separation ?? 0.55) * 0.95;
  const pointSize = settings.pointSize ?? 1;
  const intensity = settings.intensity ?? 1;

  for (const p of points) {
    const bx = (p.x / Math.max(1, imgWidth - 1)) * (bins - 1);
    const by = (p.y / Math.max(1, imgHeight - 1)) * (bins - 1);
    const sigma = clamp((bins / 24) * pointSize * separationFactor, 0.7, bins / 6);
    const weight = (p.weight > 0 ? p.weight : 1) * intensity;
    accumulateGaussian(matrix, bins, bx, by, sigma, weight);
  }

  let maxValue = 0;
  for (let i = 0; i < matrix.length; i += 1) {
    if (matrix[i] > maxValue) maxValue = matrix[i];
  }
  return { matrix, bins, maxValue };
}

/** Rampa azul → cyan → teal → gold → naranja (tool-heatmap). */
export function heatColor(value) {
  const t = clamp(value, 0, 1);
  const stops = [
    { t: 0, c: [20, 44, 120, 0] },
    { t: 0.2, c: [0, 140, 255, 0.2] },
    { t: 0.45, c: [0, 220, 180, 0.34] },
    { t: 0.7, c: [255, 215, 0, 0.5] },
    { t: 1, c: [255, 72, 0, 0.72] },
  ];

  const right = stops.find((stop) => stop.t >= t) ?? stops[stops.length - 1];
  const rightIndex = stops.indexOf(right);
  const left = rightIndex > 0 ? stops[rightIndex - 1] : right;
  const range = Math.max(1e-9, right.t - left.t);
  const k = clamp((t - left.t) / range, 0, 1);

  const r = Math.round(left.c[0] + (right.c[0] - left.c[0]) * k);
  const g = Math.round(left.c[1] + (right.c[1] - left.c[1]) * k);
  const b = Math.round(left.c[2] + (right.c[2] - left.c[2]) * k);
  const a = left.c[3] + (right.c[3] - left.c[3]) * k;
  return [r, g, b, a];
}

export const HEATMAP_STYLE_GRADIENT =
  'linear-gradient(to right, rgb(20,44,120), rgb(0,140,255), rgb(0,220,180), rgb(255,215,0), rgb(255,72,0))';

export function getStyleConfig(style, userBlurPx, separation = 0.55) {
  const effectiveBlur = userBlurPx * (1.2 - separation * 0.85);
  if (style === 'sharp') {
    return { gamma: 1.05, quantizeSteps: 0, blurPx: Math.max(0, effectiveBlur * 0.25), smoothing: false };
  }
  if (style === 'bands') {
    return { gamma: 0.9, quantizeSteps: 7, blurPx: Math.max(0, effectiveBlur * 0.15), smoothing: false };
  }
  if (style === 'pixel') {
    return { gamma: 0.8, quantizeSteps: 0, blurPx: 0, smoothing: false, bins: 72 };
  }
  return { gamma: 0.55, quantizeSteps: 0, blurPx: effectiveBlur, smoothing: true, bins: 96 };
}

function normalizeForStyle(value, maxValue, styleConfig) {
  if (maxValue <= 0 || value <= 0) return 0;
  let normalized = Math.pow(value / maxValue, styleConfig.gamma);
  if (styleConfig.quantizeSteps > 1) {
    const step = 1 / styleConfig.quantizeSteps;
    normalized = Math.ceil(normalized / step) * step;
  }
  return clamp(normalized, 0, 1);
}

export function drawHeatmapPixels(matrix, bins, maxValue, opacityScale, styleConfig) {
  const lowResCanvas = document.createElement('canvas');
  lowResCanvas.width = bins;
  lowResCanvas.height = bins;
  const lowResCtx = lowResCanvas.getContext('2d');
  const image = lowResCtx.createImageData(bins, bins);

  for (let by = 0; by < bins; by += 1) {
    for (let bx = 0; bx < bins; bx += 1) {
      const value = matrix[by * bins + bx];
      if (value <= 0) continue;
      const normalized = normalizeForStyle(value, maxValue, styleConfig);
      const [r, g, b, a] = heatColor(normalized);
      const idx = (by * bins + bx) * 4;
      image.data[idx] = r;
      image.data[idx + 1] = g;
      image.data[idx + 2] = b;
      image.data[idx + 3] = Math.round(a * 255 * opacityScale);
    }
  }
  lowResCtx.putImageData(image, 0, 0);
  return lowResCanvas;
}

/**
 * Dibuja heatmap de matriz sobre ctx (ya con fondo).
 * points en coords de imagen natural; canvas puede estar escalado.
 */
export function renderMatrixHeatmap(ctx, points, imgWidth, imgHeight, canvasWidth, canvasHeight, settings) {
  const style = settings.heatmapStyle || 'smooth';
  const styleConfig = getStyleConfig(style, settings.blur ?? 7, settings.separation ?? 0.55);
  const bins = styleConfig.bins ?? (style === 'pixel' ? 72 : 96);

  const { matrix, maxValue } = buildPointsHeatmapMatrix(
    points,
    imgWidth,
    imgHeight,
    settings,
    bins
  );

  if (maxValue <= 0) return { maxValue: 0 };

  const lowResCanvas = drawHeatmapPixels(
    matrix,
    bins,
    maxValue,
    settings.overlayOpacity ?? 1,
    styleConfig
  );

  ctx.save();
  ctx.imageSmoothingEnabled = styleConfig.smoothing;
  ctx.filter = styleConfig.blurPx > 0 ? `blur(${styleConfig.blurPx}px)` : 'none';
  ctx.drawImage(lowResCanvas, 0, 0, canvasWidth, canvasHeight);
  ctx.restore();

  return { maxValue };
}
